import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    CheckCircle,
    Info,
    Search,
    X,
    XCircle
} from "lucide-react";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useDebounce} from "../../../shared/hooks";
import ArvoreDeRecursos from "./ArvoreDeRecursos";

const createPermissionId = (resourceId, index = 0) => `perm-${resourceId}-${index}`;

export default function PermissionDialog({
    open,
    form,
    references,
    recursosDisponiveis,
    selectedLicenca,
    saving,
    editing,
    onClose,
    onSubmit,
}) {
    const viewMode = Boolean(form?.viewMode);
    const [formData, setFormData] = useState(form);
    const [errors, setErrors] = useState({});
    const [searchPermission, setSearchPermission] = useState('');
    const [filterMethod, setFilterMethod] = useState('TODOS');
    const [filterStatus, setFilterStatus] = useState('TODOS');
    const debouncedSearchPermission = useDebounce(searchPermission, 300);

    useEffect(() => {
        setFormData(form);
        setErrors({});
    }, [form]);

    const activeLicenca = selectedLicenca || references.licencas.find((item) => item.id === formData?.id_licenca) || null;

    const validate = () => {
        const nextErrors = {};
        if (!(formData.roleName || formData.nome || '').trim()) nextErrors.roleName = 'Nome da role é obrigatório';
        if (!(formData.description || formData.descricao || '').trim()) nextErrors.description = 'Descrição é obrigatória';
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const updatePermissions = (updater) => {
        setFormData((current) => {
            const nextPermissions = updater(current.permissions || []);
            return {
                ...current,
                permissions: nextPermissions,
                acoes: nextPermissions.filter((item) => item.allowed).map((item) => item.nome || item.resource?.resourceName).filter(Boolean),
            };
        });
    };

    const isRecursoAllowed = useCallback((resourceId) => {
        const permission = (formData.permissions || []).find((item) => item.resource?.id === resourceId);
        return permission?.allowed || false;
    }, [formData.permissions]);

    const togglePermissao = (resource) => {
        if (viewMode) return;
        updatePermissions((currentPermissions) => {
            const existingIndex = currentPermissions.findIndex((item) => item.resource?.id === resource.id);
            if (existingIndex >= 0) {
                const next = [...currentPermissions];
                next[existingIndex] = {...next[existingIndex], allowed: !next[existingIndex].allowed};
                return next;
            }
            return [...currentPermissions, {
                id: createPermissionId(resource.id, currentPermissions.length),
                nome: resource.resourceName,
                allowed: true,
                resource,
            }];
        });
    };

    const definirTodos = (recursos, allowed) => {
        if (viewMode) return;
        updatePermissions((currentPermissions) => {
            const next = [...currentPermissions];
            recursos.forEach((resource) => {
                const existingIndex = next.findIndex((item) => item.resource?.id === resource.id);
                if (existingIndex >= 0) next[existingIndex] = {...next[existingIndex], allowed};
                else next.push({id: createPermissionId(resource.id, next.length), nome: resource.resourceName, allowed, resource});
            });
            return next;
        });
    };

    const permissionStats = useMemo(() => {
        const liberadas = (formData.permissions || []).filter((item) => item.allowed).length;
        const bloqueadas = (formData.permissions || []).filter((item) => !item.allowed).length;
        const total = (formData.permissions || []).length;
        return {liberadas, bloqueadas, total};
    }, [formData.permissions]);

    const filteredRecursos = useMemo(() => recursosDisponiveis.map((modulo) => ({
        ...modulo,
        funcionalidades: modulo.funcionalidades.map((funcionalidade) => ({
            ...funcionalidade,
            recursos: funcionalidade.recursos.filter((recurso) => {
                const matchesSearch = !debouncedSearchPermission
                    || recurso.resourceName.toLowerCase().includes(debouncedSearchPermission.toLowerCase())
                    || recurso.endpoint.toLowerCase().includes(debouncedSearchPermission.toLowerCase());
                const matchesMethod = filterMethod === 'TODOS' || recurso.httpMethod === filterMethod;
                const allowed = isRecursoAllowed(recurso.id);
                const matchesStatus = filterStatus === 'TODOS'
                    || (filterStatus === 'LIBERADOS' && allowed)
                    || (filterStatus === 'BLOQUEADOS' && !allowed);
                return matchesSearch && matchesMethod && matchesStatus;
            }),
        })),
    })), [debouncedSearchPermission, filterMethod, filterStatus, recursosDisponiveis, isRecursoAllowed]);

    const handleSubmit = () => {
        if (viewMode) {
            onClose();
            return;
        }
        if (!validate()) return;
        onSubmit({
            ...formData,
            nome: formData.roleName,
            roleName: formData.roleName,
            descricao: formData.description,
            description: formData.description,
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen>
            <Box sx={{position: 'fixed', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: {xs: 'flex-end', sm: 'center'}, justifyContent: 'center', p: {xs: 0, sm: 2}}}>
                <Box sx={{bgcolor: '#fff', borderRadius: {xs: '16px 16px 0 0', sm: 2}, boxShadow: 24, width: '100%', maxWidth: 1200, maxHeight: {xs: '95vh', sm: '90vh'}, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb'}}>
                        <Box>
                            <Typography variant="h5" sx={{color: '#111827'}}>
                                {viewMode ? 'Visualizar Role' : editing ? 'Editar Role' : 'Nova Role'}
                            </Typography>
                            <Typography variant="body2" sx={{color: '#6b7280', mt: 0.5}}>
                                {viewMode ? 'Visualize as permissões configuradas para esta role' : 'Configure as permissões disponíveis para o perfil de acesso'}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose}>
                            <X size={20} color="#6b7280" />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{p: 3, overflowY: 'auto'}}>
                        <Stack spacing={4}>
                            <Box>
                                <Typography sx={{color: '#111827', mb: 2}}>Dados Gerais da Role</Typography>
                                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                                    <Box sx={{gridColumn: {md: '1 / span 2'}}}>
                                        <Typography variant="body2" sx={{color: '#374151', mb: 1}}>Nome da Role <Box component="span" sx={{color: '#ef4444'}}>*</Box></Typography>
                                        <TextField
                                            value={formData.roleName || formData.nome || ''}
                                            onChange={(event) => setFormData((current) => ({...current, roleName: event.target.value, nome: event.target.value}))}
                                            disabled={viewMode}
                                            fullWidth
                                            placeholder="Ex: Operador Financeiro"
                                            error={Boolean(errors.roleName)}
                                        />
                                        {errors.roleName ? <Typography variant="caption" sx={{color: '#ef4444', mt: 0.5, display: 'block'}}>{errors.roleName}</Typography> : null}
                                    </Box>
                                    <Box sx={{gridColumn: {md: '1 / span 2'}}}>
                                        <Typography variant="body2" sx={{color: '#374151', mb: 1}}>Descrição <Box component="span" sx={{color: '#ef4444'}}>*</Box></Typography>
                                        <TextField
                                            value={formData.description || formData.descricao || ''}
                                            onChange={(event) => setFormData((current) => ({...current, description: event.target.value, descricao: event.target.value}))}
                                            disabled={viewMode}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            placeholder="Descreva as responsabilidades e escopo desta role..."
                                            error={Boolean(errors.description)}
                                        />
                                        {errors.description ? <Typography variant="caption" sx={{color: '#ef4444', mt: 0.5, display: 'block'}}>{errors.description}</Typography> : null}
                                    </Box>
                                </Box>
                            </Box>

                            <Box>
                                <Typography sx={{color: '#111827', mb: 2}}>Escopo da Licença</Typography>
                                <Box sx={{bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2, p: 2}}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Info size={20} color="#2563eb" style={{marginTop: 2}} />
                                        <Box>
                                            <Typography variant="body2" sx={{color: '#1e3a8a', mb: 1}}>
                                                Esta role pertence à licença <strong>{(activeLicenca?.id || '').slice(0, 13)}{activeLicenca?.id ? '...' : ''}</strong> da empresa <strong>{activeLicenca?.empresa?.razao_social || activeLicenca?.empresa?.nome || '-'}</strong>.
                                            </Typography>
                                            <Typography variant="body2" sx={{color: '#1e3a8a'}}>
                                                Somente recursos disponíveis no plano <strong>{activeLicenca?.plano?.nome_plano || '-'}</strong> podem ser configurados.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>

                            <Box>
                                <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" spacing={2} sx={{mb: 2}}>
                                    <Typography sx={{color: '#111827'}}>Permissões da Role</Typography>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CheckCircle size={16} color="#16a34a" />
                                            <Typography variant="body2" sx={{color: '#374151'}}>{permissionStats.liberadas} liberadas</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <XCircle size={16} color="#dc2626" />
                                            <Typography variant="body2" sx={{color: '#374151'}}>{permissionStats.bloqueadas} bloqueadas</Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>

                                <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} sx={{mb: 2}}>
                                    <TextField
                                        placeholder="Buscar por recurso ou endpoint..."
                                        value={searchPermission}
                                        onChange={(event) => setSearchPermission(event.target.value)}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={16} color="#9ca3af" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField select value={filterMethod} onChange={(event) => setFilterMethod(event.target.value)} sx={{minWidth: 180}}>
                                        <MenuItem value="TODOS">Todos os métodos</MenuItem>
                                        <MenuItem value="GET">GET</MenuItem>
                                        <MenuItem value="POST">POST</MenuItem>
                                        <MenuItem value="PUT">PUT</MenuItem>
                                        <MenuItem value="PATCH">PATCH</MenuItem>
                                        <MenuItem value="DELETE">DELETE</MenuItem>
                                    </TextField>
                                    <TextField select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)} sx={{minWidth: 160}}>
                                        <MenuItem value="TODOS">Todos</MenuItem>
                                        <MenuItem value="LIBERADOS">Liberados</MenuItem>
                                        <MenuItem value="BLOQUEADOS">Bloqueados</MenuItem>
                                    </TextField>
                                </Stack>

                                <ArvoreDeRecursos
                                    modulos={filteredRecursos}
                                    isLiberado={isRecursoAllowed}
                                    onAlternar={togglePermissao}
                                    onDefinirTodos={definirTodos}
                                    somenteLeitura={viewMode}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>

                    <Box sx={{px: 3, py: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                        <Typography variant="body2" sx={{color: '#6b7280'}}>
                            {permissionStats.total > 0 ? `${permissionStats.total} ${permissionStats.total === 1 ? 'recurso configurado' : 'recursos configurados'}` : ''}
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                            <Button variant="outlined" onClick={onClose} sx={{textTransform: 'none'}}>
                                {viewMode ? 'Fechar' : 'Cancelar'}
                            </Button>
                            {!viewMode ? (
                                <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{textTransform: 'none', bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}>
                                    {editing ? 'Salvar Alterações' : 'Criar Role'}
                                </Button>
                            ) : null}
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}
